import classNames from 'classnames';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { isKeyboardFocus } from '../../utils/is-keyboard-focus';
import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './RevelioTooltip.module.css';

export type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

export interface RevelioTooltipProps {
  /** Tooltip content. Nothing renders when this is empty. */
  label: ReactNode;
  /** The trigger. Wrapped in an inline-block span that owns the hover/focus. */
  children: ReactNode;
  placement?: TooltipPlacement;
  /** ms to wait before opening on hover. Keyboard focus opens immediately. */
  openDelay?: number;
  isDisabled?: boolean;
  /**
   * Stays open while the pointer is inside the tooltip itself, so its content
   * can be scrolled or selected. Leaving the trigger then closes after a short
   * grace period rather than at once, long enough to cross the gap.
   */
  isInteractive?: boolean;
  /** Controlled visibility. When set, hover/focus no longer toggle. */
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  maxWidth?: number | string;
  /** Gap between trigger and tooltip, in px. */
  offset?: number;
  /**
   * Shift along the alignment axis, in px — horizontal for top/bottom
   * placements, vertical for left/right. Nudges a tooltip whose trigger sits at
   * the edge of a toolbar.
   */
  crossOffset?: number;
  /** Multi-line hints often centre; single labels stay left. */
  textAlign?: 'left' | 'center';
  /** Applied to the floating tooltip, not the trigger wrapper. */
  className?: string;
  /**
   * Make the trigger wrapper a block that fills its container. The default
   * inline-block wrapper shrink-wraps, which collapses full-width children
   * (menu items, cards) that size themselves off their parent.
   */
  fullWidthTrigger?: boolean;
  /**
   * Applied to the trigger wrapper. The wrapper is a real box in the parent's
   * layout, so a flex/grid parent sizes IT, not the child — anything the child
   * needs from the parent (`flex`, `min-width: 0`, grid placement) has to land
   * here or it is inert. Use when `fullWidthTrigger` is too blunt.
   */
  triggerClassName?: string;
  /**
   * Called once each time the tooltip becomes visible. The analytics hook —
   * this library does no tracking of its own, so the caller names what opened.
   */
  onOpen?: () => void;
}

const DEFAULT_OFFSET = 8;
const ARROW_INSET = 12;

type Position = { top: number; left: number; arrowOffset: number };

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

const side = (placement: TooltipPlacement) =>
  placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';

const align = (placement: TooltipPlacement) =>
  (placement.split('-')[1] ?? 'center') as 'start' | 'end' | 'center';

function computePosition(
  trigger: DOMRect,
  tooltip: DOMRect,
  placement: TooltipPlacement,
  offset: number,
  crossOffset: number
): Position {
  const s = side(placement);
  const a = align(placement);

  const alignAlong = (
    triggerStart: number,
    triggerSize: number,
    size: number
  ) => {
    if (a === 'start') return triggerStart;
    if (a === 'end') return triggerStart + triggerSize - size;
    return triggerStart + (triggerSize - size) / 2;
  };

  let top: number;
  let left: number;

  if (s === 'top' || s === 'bottom') {
    top =
      s === 'top'
        ? trigger.top - tooltip.height - offset
        : trigger.bottom + offset;
    left = alignAlong(trigger.left, trigger.width, tooltip.width) + crossOffset;
  } else {
    left =
      s === 'left'
        ? trigger.left - tooltip.width - offset
        : trigger.right + offset;
    top = alignAlong(trigger.top, trigger.height, tooltip.height) + crossOffset;
  }

  // Clamp into the viewport. Deliberately a clamp rather than a flip: these are
  // short labels, and a flip would move the tooltip out from under the cursor.
  const { clientWidth, clientHeight } = document.documentElement;
  const maxLeft = clientWidth - tooltip.width;
  const maxTop = clientHeight - tooltip.height;
  const finalLeft = clamp(left, 0, Math.max(maxLeft, 0));
  const finalTop = clamp(top, 0, Math.max(maxTop, 0));

  const horizontal = s === 'top' || s === 'bottom';
  const arrowSpan = horizontal ? tooltip.width : tooltip.height;
  const triggerCentre = horizontal
    ? trigger.left + trigger.width / 2
    : trigger.top + trigger.height / 2;
  const inset = Math.min(ARROW_INSET, arrowSpan / 2);
  const arrowOffset = clamp(
    triggerCentre - (horizontal ? finalLeft : finalTop),
    inset,
    Math.max(arrowSpan - inset, inset)
  );

  return { left: finalLeft, top: finalTop, arrowOffset };
}

/**
 * The dashboard's tooltip. Hand-rolled rather than wrapping a positioning
 * library: the surface is a short label on hover/focus, so a trigger rect plus a
 * viewport clamp covers it.
 *
 * Positioned with `fixed` off `getBoundingClientRect`, portalled to the
 * `UIPortalContext` target so it escapes overflow/stacking contexts.
 */
const INTERACTIVE_CLOSE_GRACE_MS = 300;

export const RevelioTooltip = ({
  label,
  children,
  placement = 'bottom',
  openDelay = 400,
  isDisabled = false,
  isOpen,
  defaultIsOpen = false,
  maxWidth,
  offset = DEFAULT_OFFSET,
  crossOffset = 0,
  textAlign,
  className,
  fullWidthTrigger = false,
  triggerClassName,
  onOpen,
  isInteractive = false,
}: RevelioTooltipProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultIsOpen);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portalTarget = useContext(UIPortalContext);
  const tooltipId = useId();

  const isControlled = isOpen !== undefined;
  const hasLabel = label !== null && label !== undefined && label !== '';
  const open =
    (isControlled ? isOpen : uncontrolledOpen) && !isDisabled && hasLabel;

  // Stable: it lands in the deps of `show`/`hide`/the unmount cleanup, and a
  // fresh identity each render would re-run that cleanup mid-delay and stop the
  // tooltip from ever opening.
  const clearOpenTimer = useCallback(() => {
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  useEffect(() => clearOpenTimer, [clearOpenTimer]);

  // Read through a ref so an inline handler (a new identity every render) does
  // not re-arm the effect: the notice is per open, not per render while open.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  useEffect(() => {
    if (open) onOpenRef.current?.();
  }, [open]);

  useEffect(() => {
    if (isDisabled || !hasLabel) {
      clearOpenTimer();
      setUncontrolledOpen(false);
    }
  }, [isDisabled, hasLabel, clearOpenTimer]);

  const show = useCallback(
    (immediate: boolean) => {
      if (isControlled || isDisabled) return;
      clearOpenTimer();
      if (immediate || openDelay === 0) {
        setUncontrolledOpen(true);
        return;
      }
      openTimer.current = setTimeout(
        () => setUncontrolledOpen(true),
        openDelay
      );
    },
    [isControlled, isDisabled, openDelay, clearOpenTimer]
  );

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const hide = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    if (!isControlled) {
      setUncontrolledOpen(false);
    }
  }, [isControlled, clearOpenTimer, clearCloseTimer]);

  // Leaving the trigger for the tooltip crosses a gap; the grace period is
  // what lets the pointer arrive before the close lands.
  const hideAfterGrace = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimer.current = setTimeout(hide, INTERACTIVE_CLOSE_GRACE_MS);
  }, [clearOpenTimer, clearCloseTimer, hide]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;
    setPosition(
      computePosition(
        trigger.getBoundingClientRect(),
        tooltip.getBoundingClientRect(),
        placement,
        offset,
        crossOffset
      )
    );
  }, [placement, offset, crossOffset]);

  // Measure after paint so the tooltip's own size is known before placing it.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  // A fixed-position tooltip does not follow the page: close on scroll or
  // resize rather than tracking it. A controlled tooltip cannot be closed from
  // here (`hide` is a no-op), so re-measure to keep it pinned to its trigger.
  useEffect(() => {
    if (!open) return;
    const onViewportChange = (event: Event) => {
      // Scrolling the tooltip's own content is not the page moving under it.
      if (
        event.target instanceof Node &&
        tooltipRef.current?.contains(event.target)
      ) {
        return;
      }
      if (isControlled) {
        updatePosition();
      } else {
        hide();
      }
    };
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    return () => {
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [open, isControlled, updatePosition, hide]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, hide]);

  const contentStyle: CSSProperties = {
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    textAlign,
    // Rendered off-screen for the measuring pass, then placed.
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    visibility: position === null ? 'hidden' : 'visible',
    ...({
      '--ui-tooltip-arrow-offset': `${position?.arrowOffset ?? 0}px`,
    } as CSSProperties),
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={classNames(
          styles.trigger,
          fullWidthTrigger && styles.triggerFullWidth,
          triggerClassName
        )}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => {
          clearCloseTimer();
          show(false);
        }}
        onMouseLeave={isInteractive ? hideAfterGrace : hide}
        onFocus={(event) => {
          if (isKeyboardFocus(event.target)) show(true);
        }}
        onBlur={hide}
        // Chakra's closeOnClick default: a click that opens another surface
        // (modal, menu) should not leave the tooltip painted above it.
        onClick={hide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            data-placement={placement}
            data-placed={position === null ? undefined : ''}
            className={classNames(
              styles.tooltip,
              isInteractive && styles.interactive,
              className
            )}
            style={contentStyle}
            onMouseEnter={isInteractive ? clearCloseTimer : undefined}
            onMouseLeave={isInteractive ? hide : undefined}
          >
            {label}
            <span className={styles.arrow} aria-hidden="true" />
          </div>,
          portalTarget
        )}
    </>
  );
};
